import { NETWORK_COLORS } from '@/constants/colors'
import { BENCHMARK_SELECTED_NETWORKS_COOKIE_KEY } from '@/constants/cookie'
import { useMixpanel } from '@/context/mixpanel'
import { useRelativeTime } from '@/hooks/analytics/useRelativeTime'
import { useAllNetworksBenchmarksQuery } from '@/queries/allNetworksBenchmarks.query'
import type { VaultApyRange } from '@/types/apy'
import type { ChartPeriod } from '@/types/periods'
import { getApyValue } from '@/utils/apy'
import { setCookieValue } from '@/utils/cookie/cookie'
import { subDays, toUnixTimestamp } from '@vaultsfyi/common/utils'
import { useCallback, useMemo } from 'react'
import {
  DialogBody,
  DialogContentWrapper,
  DialogDescription,
  DialogHeader,
  DialogHeaderWrapper,
  DialogScrollArea,
  DialogTitle,
} from '../common/dialog'
import { Separator } from '../common/separator'
import { BenchmarkNetworksChart } from './benchmarkNetworksChart'
import { BenchmarkNetworksSelect } from './benchmarkNetworksSelect'

const PRIORITY_NETWORKS = ['mainnet', 'base', 'plasma', 'arbitrum', 'bnb']

interface Props {
  apyRange: VaultApyRange
  includeRewards: boolean
  chartPeriod: ChartPeriod
  selectedNetworks: Set<string>
  onSelectedNetworksChange: (networks: Set<string>) => void
}

export const BenchmarksFullView = ({
  apyRange,
  includeRewards,
  chartPeriod,
  selectedNetworks,
  onSelectedNetworksChange,
}: Props) => {
  const { trackEvent } = useMixpanel()

  const { data: usdData } = useAllNetworksBenchmarksQuery({ code: 'usd' })
  const { data: ethData } = useAllNetworksBenchmarksQuery({ code: 'eth' })

  const allNetworks = useMemo(() => {
    const networks = new Set([...Object.keys(usdData ?? {}), ...Object.keys(ethData ?? {})])
    return Array.from(networks)
  }, [usdData, ethData])

  const colorMap = useMemo(() => {
    const map: Record<string, string> = {}
    for (const network of allNetworks) {
      const color = NETWORK_COLORS[network as keyof typeof NETWORK_COLORS]
      if (color) map[network] = color
    }
    return map
  }, [allNetworks])

  const availableNetworks = useMemo(() => {
    const getLastApy = (network: string): number => {
      const data = usdData?.[network] ?? ethData?.[network] ?? []
      if (data.length === 0) return 0
      const last = data.reduce((a, b) => (b.timestamp > a.timestamp ? b : a))
      return last.apy ? getApyValue({ apy: last.apy, apyRange, includeRewards }) : 0
    }

    return [...allNetworks].sort((a, b) => {
      const ai = PRIORITY_NETWORKS.indexOf(a)
      const bi = PRIORITY_NETWORKS.indexOf(b)
      if (ai !== -1 && bi !== -1) return ai - bi
      if (ai !== -1) return -1
      if (bi !== -1) return 1
      const diff = getLastApy(b) - getLastApy(a)
      return diff !== 0 ? diff : a.localeCompare(b)
    })
  }, [allNetworks, usdData, ethData, apyRange, includeRewards])

  const handleSelectedNetworksChange = useCallback(
    (networks: Set<string>) => {
      setCookieValue(BENCHMARK_SELECTED_NETWORKS_COOKIE_KEY, [...networks])
      onSelectedNetworksChange(networks)
      trackEvent('Benchmark network selection changed', { Networks: [...networks] })
    },
    [onSelectedNetworksChange, trackEvent]
  )

  const sharedYDomain = useMemo(() => {
    const cutoff = Math.floor(toUnixTimestamp(subDays(Date.now(), chartPeriod)))
    const values = [usdData, ethData].flatMap((data) =>
      [...selectedNetworks].flatMap((network) =>
        (data?.[network] ?? [])
          .filter((p) => p.timestamp >= cutoff && p.apy)
          .map((p) => getApyValue({ apy: p.apy!, apyRange, includeRewards }))
      )
    )
    if (values.length === 0) return undefined
    const min = values.reduce((a, b) => Math.min(a, b), Infinity)
    const max = values.reduce((a, b) => Math.max(a, b), -Infinity)
    const step = 0.02
    return [Math.floor(min / step) * step, Math.ceil(max / step) * step] as [number, number]
  }, [usdData, ethData, selectedNetworks, chartPeriod, apyRange, includeRewards])

  const lastUpdateTimestamp = useMemo(() => {
    const allPoints = [...Object.values(usdData ?? {}), ...Object.values(ethData ?? {})].flat()
    if (allPoints.length === 0) return null
    return allPoints.reduce((max, p) => Math.max(max, p.timestamp), -Infinity)
  }, [usdData, ethData])

  const relativeTime = useRelativeTime(lastUpdateTimestamp)

  return (
    <DialogBody>
      <DialogHeaderWrapper className="pt-6 md:pt-8">
        <DialogHeader>
          <DialogTitle>Vaults.fyi benchmark rates</DialogTitle>
          <DialogDescription>
            Measures average APY across largest markets on each network
            {relativeTime !== null && ` · Updated ${relativeTime}`}
          </DialogDescription>
        </DialogHeader>
        <Separator />
      </DialogHeaderWrapper>
      <DialogScrollArea>
        <DialogContentWrapper className="pt-4 md:pt-4">
          <div className="flex items-center gap-4">
            <p className="font-semibold text-heading-foreground">Selected networks</p>
            <BenchmarkNetworksSelect
              availableNetworks={availableNetworks}
              selectedNetworks={selectedNetworks}
              onSelectedNetworksChange={handleSelectedNetworksChange}
              showLabel={false}
            />
          </div>
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <BenchmarkNetworksChart
              code="usd"
              apyRange={apyRange}
              includeRewards={includeRewards}
              period={chartPeriod}
              selectedNetworks={selectedNetworks}
              colorMap={colorMap}
              yDomain={sharedYDomain}
            />
            <BenchmarkNetworksChart
              code="eth"
              apyRange={apyRange}
              includeRewards={includeRewards}
              period={chartPeriod}
              selectedNetworks={selectedNetworks}
              colorMap={colorMap}
              yDomain={sharedYDomain}
            />
          </div>
        </DialogContentWrapper>
      </DialogScrollArea>
    </DialogBody>
  )
}
